import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, Form, message } from 'antd';
import { CheckCircleFilled, SyncOutlined, DownOutlined, UpOutlined, CopyOutlined } from '@ant-design/icons';
import { verifyEhrConnection, createEhrConnection } from 'api/ehr';
import { TEhrVerifyType, TEhrStepsConfig, TEhrExternalInfoField, TEhrLoginField } from 'types/ehrConnection';
import EhrStepGuide from './EhrStepGuide';
import VerifyResultCard from './VerifyResultCard';
import s from './s.module.less';

type Props = {
    ehrName: string;
    verifyType: TEhrVerifyType;
    stepsConfig?: TEhrStepsConfig;
    agentEmail: string;
    externalInfoFields?: TEhrExternalInfoField[];
    // account_password 的登录名输入框（Dr. Chrono 是 username、EMR-Bear 是 username 或 email）
    loginField?: TEhrLoginField;
    onSuccess: () => void;
    onBack: () => void;
};

type VerifyState = 'idle' | 'verifying' | 'success' | 'failed';

// AskChart 有时把 EHR 页面提示框的关闭按钮「×」一起抓进报错（如 EMR-Bear 的 "Wrong credentials. ×"），展示前去掉
const cleanVerifyError = (msg: string) => msg.replace(/[\s×✕✖]+$/u, '').trim();

const VerifyStep = ({
    ehrName,
    verifyType,
    stepsConfig,
    agentEmail,
    externalInfoFields,
    loginField,
    onSuccess,
    onBack,
}: Props) => {
    const [state, setState] = useState<VerifyState>('idle');
    const [showSteps, setShowSteps] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        if (state === 'success') {
            const timer = setTimeout(onSuccess, 2000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [state, onSuccess]);

    const doVerify = useCallback(async () => {
        setState('verifying');
        setError(null);
        try {
            const res = await verifyEhrConnection({ ehrSystem: ehrName });
            if (res && !res.error) {
                const data = res.data?.data;
                if (data?.verified || data?.connectionStatus === 'connected') {
                    setState('success');
                } else {
                    setState('failed');
                    setError(
                        (data?.error && cleanVerifyError(data.error)) ||
                            'Connection could not be verified. Please check the setup steps and try again.',
                    );
                }
            } else {
                setState('failed');
                setError('Verification request failed. Please try again.');
            }
        } catch {
            setState('failed');
            setError('An unexpected error occurred. Please try again.');
        }
    }, [ehrName]);

    const handlePasswordSubmit = async () => {
        try {
            const values = await form.validateFields();
            setState('verifying');
            setError(null);

            const payload: {
                ehrSystem: string;
                password: string;
                email?: string;
                externalInfo?: Record<string, string>;
            } = {
                ehrSystem: ehrName,
                password: values.password,
            };

            // 登录名统一放 email 字段（同 AskChart）：username 登录的 EHR 由后端豁免邮箱格式校验
            if (values.email) {
                payload.email = String(values.email).trim();
            }

            if (externalInfoFields?.length) {
                const ext: Record<string, string> = {};
                externalInfoFields.forEach((f) => {
                    if (values[f.key]) ext[f.key] = values[f.key];
                });
                if (Object.keys(ext).length) payload.externalInfo = ext;
            }

            const createRes = await createEhrConnection(payload);
            if (!createRes || createRes.error) {
                setState('failed');
                setError(
                    createRes?.error || 'Failed to create connection. Please check your credentials and try again.',
                );
                return;
            }

            const verifyRes = await verifyEhrConnection({ ehrSystem: ehrName });
            if (verifyRes && !verifyRes.error) {
                const data = verifyRes.data?.data;
                if (data?.verified || data?.connectionStatus === 'connected') {
                    setState('success');
                } else {
                    setState('failed');
                    setError(
                        (data?.error && cleanVerifyError(data.error)) ||
                            'Connection could not be verified. Please check your credentials and try again.',
                    );
                }
            } else {
                setState('failed');
                setError('Verification request failed. Please try again.');
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            if (e?.errorFields) return;
            setState('failed');
            setError('An unexpected error occurred.');
        }
    };

    if (state === 'success') {
        return (
            <div className={s.verifySuccess}>
                <div className={s.verifySuccessIcon}>
                    <CheckCircleFilled />
                </div>
                <div>
                    <div className={s.verifySuccessTitle}>{ehrName} connected successfully!</div>
                    <div className={s.verifySuccessDesc}>
                        Klarity now has access to your {ehrName} account and can sync your data.
                    </div>
                </div>
            </div>
        );
    }

    if (verifyType === 'agent_auto') {
        // 原型 V3：先在 double-check 页确认已加好 agent，点确认后才去验证（验证本身与线上一致）
        if (state === 'idle') {
            return (
                <>
                    <h2 className={s.flowTitle}>Confirm the agent was added to {ehrName}</h2>
                    <p className={s.flowSubtitle}>
                        Klarity cannot see your {ehrName} account until the invite arrives.
                    </p>
                    <div className={s.verifyCard}>
                        <div className={s.verifyTitle}>Double-check in {ehrName} before you confirm</div>
                        <div className={s.verifyEmailRow}>
                            <div className={s.verifyEmailDisplay}>{agentEmail}</div>
                            <Button
                                icon={<CopyOutlined />}
                                onClick={() => {
                                    navigator.clipboard.writeText(agentEmail);
                                    message.success('Email copied');
                                }}
                            >
                                Copy
                            </Button>
                        </div>
                        <div className={s.verifySub}>
                            Make sure the user exists with this email, the invite was sent, and two-factor
                            authentication is off for it.
                        </div>
                        <span className={s.verifyShowSteps} onClick={() => setShowSteps(!showSteps)}>
                            {showSteps ? 'Hide steps' : 'Show full steps'}
                            {showSteps ? <UpOutlined /> : <DownOutlined />}
                        </span>
                        {showSteps && stepsConfig && (
                            <div style={{ marginTop: 16, textAlign: 'left' }}>
                                <EhrStepGuide config={stepsConfig} agentEmail={agentEmail} />
                            </div>
                        )}
                        <div className={s.verifyActions} style={{ marginTop: 24 }}>
                            <Button type="primary" onClick={doVerify}>
                                I checked, the agent is added
                            </Button>
                        </div>
                    </div>
                </>
            );
        }
        return (
            <>
                <h2 className={s.flowTitle}>Verify your {ehrName} connection</h2>
                <p className={s.flowSubtitle}>Klarity is checking that it can access your {ehrName} account.</p>
                {state === 'verifying' && (
                    <div className={s.verifyCard}>
                        <div className={s.verifyIconBlue}>
                            <SyncOutlined spin style={{ fontSize: 28 }} />
                        </div>
                        <div className={s.verifyTitle}>Verifying connection to {ehrName}...</div>
                        <div className={s.verifySub}>This usually takes a few seconds</div>
                        <div className={s.verifyProgress}>
                            <div className={s.verifyProgressBar} />
                        </div>
                    </div>
                )}
                {state === 'failed' && (
                    <VerifyResultCard
                        tone="fail"
                        title="Connection verification failed"
                        desc={error}
                        agentEmail={agentEmail}
                        stepsConfig={stepsConfig}
                        onBack={onBack}
                        onRetry={doVerify}
                    />
                )}
            </>
        );
    }

    if (verifyType === 'agent_password' || verifyType === 'account_password') {
        const isAccountMode = verifyType === 'account_password';
        let cardTitle = 'Enter the password you created for the agent account';
        let cardSub = 'The password you set when creating the agent user in your EHR.';
        if (isAccountMode && loginField) {
            // Dr. Chrono / EMR-Bear（同 AskChart account_password）：agent 账号的登录名 + 密码
            cardTitle = 'Enter the login details for the agent account';
            cardSub = `The login and password you set when creating the agent user in ${ehrName}.`;
        } else if (isAccountMode) {
            cardTitle = `Enter your ${ehrName} login credentials`;
            cardSub = 'We need your account credentials to connect to your EHR.';
        } else if (externalInfoFields?.length) {
            cardTitle = 'Enter the details for the agent account';
            cardSub = `Your ${ehrName} address, and the password you set when creating the agent user.`;
        }
        if (state === 'failed') {
            // 同邀请型的失败页；Retry 回到表单（已填内容保留，改完再 Connect）
            return (
                <>
                    <h2 className={s.flowTitle}>Verify your {ehrName} connection</h2>
                    <p className={s.flowSubtitle}>Klarity is checking that it can access your {ehrName} account.</p>
                    <VerifyResultCard
                        tone="fail"
                        title="Connection verification failed"
                        desc={error}
                        agentEmail={agentEmail}
                        stepsConfig={stepsConfig}
                        onBack={onBack}
                        onRetry={() => {
                            setError(null);
                            setState('idle');
                        }}
                    />
                </>
            );
        }
        return (
            <>
                <h2 className={s.flowTitle}>Verify your {ehrName} connection</h2>
                <p className={s.flowSubtitle}>Klarity is checking that it can access your {ehrName} account.</p>
                <div className={s.verifyCard}>
                    {state === 'verifying' ? (
                        <>
                            <div className={s.verifyIconBlue}>
                                <SyncOutlined spin style={{ fontSize: 28 }} />
                            </div>
                            <div className={s.verifyTitle}>Connecting to {ehrName}...</div>
                            <div className={s.verifySub}>Verifying your credentials</div>
                            <div className={s.verifyProgress}>
                                <div className={s.verifyProgressBar} />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={s.verifyTitle}>{cardTitle}</div>
                            <div className={s.verifySub}>{cardSub}</div>
                            <Form form={form} layout="vertical" className={s.verifyFormWrap}>
                                {isAccountMode && (
                                    <Form.Item
                                        name="email"
                                        label={
                                            <span className={s.verifyFormLabel}>{loginField?.label || 'Email'}</span>
                                        }
                                        rules={[
                                            {
                                                required: loginField?.required ?? true,
                                                message: `${loginField?.label || 'Email'} is required`,
                                            },
                                            ...(loginField?.email
                                                ? [{ type: 'email' as const, message: 'Enter a valid email address' }]
                                                : []),
                                        ]}
                                    >
                                        <Input
                                            size="large"
                                            placeholder={loginField?.placeholder || 'your-email@example.com'}
                                            autoComplete="off"
                                        />
                                    </Form.Item>
                                )}
                                {externalInfoFields?.map((field) => (
                                    <Form.Item
                                        key={field.key}
                                        name={field.key}
                                        label={<span className={s.verifyFormLabel}>{field.label}</span>}
                                        extra={field.help}
                                        getValueFromEvent={(e) =>
                                            field.normalize ? field.normalize(e.target.value) : e.target.value
                                        }
                                        rules={[
                                            ...(field.required
                                                ? [{ required: true, message: `${field.label} is required` }]
                                                : []),
                                            ...(field.pattern
                                                ? [{ pattern: field.pattern.regex, message: field.pattern.message }]
                                                : []),
                                        ]}
                                    >
                                        <Input
                                            size="large"
                                            placeholder={field.placeholder}
                                            type={field.type}
                                            addonBefore={field.addonBefore}
                                            addonAfter={field.addonAfter}
                                        />
                                    </Form.Item>
                                ))}
                                <Form.Item
                                    name="password"
                                    label={<span className={s.verifyFormLabel}>Password</span>}
                                    rules={[{ required: true, message: 'Password is required' }]}
                                >
                                    <Input.Password
                                        size="large"
                                        placeholder="Enter password"
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                            </Form>
                            <div className={s.verifyActions}>
                                <Button onClick={onBack}>Go back</Button>
                                <Button type="primary" onClick={handlePasswordSubmit}>
                                    Connect
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </>
        );
    }

    return null;
};

export default VerifyStep;
